import React, { useEffect, useState, useContext } from 'react';
import { Container, Box, Card, CardContent, Avatar, Rating, Grid } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import '../styles/css/Testimonials.css';
// Import animation components
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LanguageContext } from '../App';

// Import testimonial images
import annaAvatar from '../styles/images/testimonials/anna.webp';
import timnaAvatar from '../styles/images/testimonials/timna.webp';
import galAvatar from '../styles/images/testimonials/gal.webp';

const Testimonials = () => {
  const [isClient, setIsClient] = useState(false);
  
  // Add animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const { language, translations } = useContext(LanguageContext);

  const testimonials = translations[language].testimonials;

  // Ensure we're running in the browser
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // Trigger animations when section becomes visible
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  const quoteIconVariants = {
    hidden: { scale: 0, opacity: 0, rotate: -180 },
    visible: {
      scale: 1, 
      opacity: 1, 
      rotate: 0,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 80
      }
    }
  };

  const renderDesktopTestimonials = () => (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      <Grid container spacing={3}>
        {testimonials.map((testimonial) => (
          <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
            <motion.div variants={itemVariants}>
              <Card className="testimonial-card" component={motion.div} whileHover={{ y: -10, boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)" }}>
                <CardContent className="testimonial-card-content">
                  <motion.div className="testimonial-quote-icon" variants={quoteIconVariants}>
                    <FormatQuoteIcon fontSize="large" />
                  </motion.div>
                  <p className="testimonial-text">
                    {testimonial.text}
                  </p>
                  <Box className="testimonial-rating">
                    <Rating value={testimonial.rating} readOnly />
                  </Box>
                  <Box className="testimonial-author">
                    <motion.div whileHover={{ scale: 1.1 }}>
                      <Avatar 
                        src={testimonial.avatar} 
                        alt={testimonial.name}
                        className="testimonial-avatar"
                      />
                    </motion.div>
                    <Box ml={2}>
                      <h3 className="testimonial-name">
                        {testimonial.name}
                      </h3>
                      <span className="testimonial-location">
                        <PlaceOutlinedIcon fontSize="inherit" />
                        {testimonial.role}
                      </span>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </motion.div>
  );

  const renderMobileTestimonials = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Carousel
        className="testimonial-swipe-carousel"
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        infiniteLoop
        swipeable
        emulateTouch
        centerMode
        centerSlidePercentage={88}
        showIndicators
      >
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="testimonial-swipe-slide">
            <Card className="testimonial-card">
              <CardContent className="testimonial-card-content">
                <Box className="testimonial-quote-icon">
                  <FormatQuoteIcon fontSize="large" />
                </Box>
                <p className="testimonial-text">
                  {testimonial.text}
                </p>
                <Box className="testimonial-rating">
                  <Rating value={testimonial.rating} readOnly />
                </Box>
                <Box className="testimonial-author">
                  <Avatar
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="testimonial-avatar"
                  />
                  <Box ml={2}>
                    <h3 className="testimonial-name">
                      {testimonial.name}
                    </h3>
                    <span className="testimonial-location">
                      <PlaceOutlinedIcon fontSize="inherit" />
                      {testimonial.role}
                    </span>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </div>
        ))}
      </Carousel>
    </motion.div>
  );

  return (
    <Box className="testimonials-section">
      <Container className="testimonials-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="section-title">
            {translations[language].testimonialsTitle}
          </h2>
          <p className="section-subtitle">
            {translations[language].testimonialsSubtitle}
          </p>
        </motion.div>
        
        {isClient && (
          <>
            <Box className="testimonials-desktop-view">
              {renderDesktopTestimonials()}
            </Box>
            
            <Box className="testimonials-mobile-view">
              {renderMobileTestimonials()}
            </Box>
          </>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <p className="testimonials-link">
            *** <a href={translations[language].testimonialsLinkUrl} target="_blank" rel="noreferrer">{translations[language].testimonialsLinkText}</a> ***
          </p>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Testimonials;
